import { getPreloadsFromPath } from './getPreloadsFromPath';
import { makeUrl } from './makeUrl';
import type { Callback, Preload, CleanPreload } from './quickload.types';
import { preloadsHash } from './preloadsHash';
import { cleanPreload } from './cleanPreload';
import { getTraceId } from './getTraceId';

import { TrelloWindow } from '@trello/window-types';
declare const window: TrelloWindow;

const parseJSON = function (data: string) {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const ajaxGetJSON = function (
  url: string,
  next: Callback,
  operationName = 'unknown',
  operationSource = 'model-loader',
  traceId?: string | null,
  taskName?: string | null,
) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.setRequestHeader('Accept', 'application/json,text/plain');
  xhr.setRequestHeader(
    'X-Trello-Client-Version',
    window.trelloVersion || 'dev-0',
  );
  xhr.setRequestHeader('X-Trello-Operation-Source', operationSource);
  xhr.setRequestHeader('X-Trello-Operation-Name', operationName);

  if (traceId) {
    xhr.setRequestHeader('X-Trello-TraceId', traceId);
    xhr.setRequestHeader('X-Trello-Task', taskName || 'not-implemented');
  }

  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      if (xhr.status !== 200) {
        next([xhr.status, xhr.responseText]);
      } else {
        next(null, [parseJSON(xhr.responseText), xhr]);
      }
    }
  };

  xhr.send(null);
};

const removePreload = function (url: string) {
  if (!(url in preloadsHash)) {
    return;
  }
  delete preloadsHash[url];
};

const preload = function (
  url: string,
  operationName = 'preload',
  traceId?: string,
) {
  if (!url) {
    return;
  }

  const preloadObject: Preload = {
    isLoading: true,
    callbacks: [],
    start: Date.now(),
    used: false,
    url,
    traceId,
  };
  preloadsHash[url] = preloadObject;

  ajaxGetJSON(
    url,
    function (err, data) {
      let callback;
      preloadObject.isLoading = false;
      if (err) {
        preloadObject.error = err;
        for (callback of Array.from(preloadObject.callbacks)) {
          callback(err);
        }
        return;
      }
      preloadObject.data = data;
      for (callback of Array.from(preloadObject.callbacks)) {
        callback(null, data);
      }
    },
    operationName,
    'quickload',
    traceId,
  );
};

const quickload = {
  // The "init" method is called once from index.ejs to kick off preloading
  init() {
    const traceId = getTraceId();
    const { preloads } = getPreloadsFromPath();
    for (const { url, operationName } of preloads) {
      preload(url, operationName, traceId);
    }
  },
  getPreloadsFromPath,
  makeUrl,
  load(
    url: string,
    next: Callback = () => {},
    operationName: string,
    operationSource: string,
    traceId?: string | null,
    taskName?: string | null,
  ): void | CleanPreload {
    const preloadObject = preloadsHash[url];
    if (preloadObject !== undefined) {
      preloadObject.used = true;
      if (preloadObject.isLoading) {
        preloadObject.callbacks.push(next);
      } else {
        next(preloadObject.error, preloadObject.data);
      }
      return cleanPreload(preloadObject);
    } else {
      ajaxGetJSON(url, next, operationName, operationSource, traceId, taskName);
    }
  },
  getPreloadTraceId(): string | undefined {
    const { preloads } = getPreloadsFromPath();
    for (const { url } of preloads) {
      if (url in preloadsHash) {
        return preloadsHash[url].traceId;
      }
    }
    return;
  },
  clear() {
    for (const url in preloadsHash) {
      removePreload(url);
    }
  },
  markComplete() {
    this.status = 'complete';
  },
  status: 'pending',
};

// dont want to run init in tests, because it can show/cause errors
// eslint-disable-next-line @trello/no-module-logic
process.env.NODE_ENV !== 'test' && quickload.init();
export const QuickLoad = (window.QuickLoad = quickload);
